import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminService } from '../admin-pages/admin.service';
import { ConfigService } from '../services/config.service';
import { FeatureAccessService } from '../services/feature-access.service';
import { SubscriptionsService } from '../services/subscriptions.service';
import { SERVICENEAR_ONBOARDING_STEPS } from './apps/servicenear-onboarding/servicenear-onboarding.config';

export type OnboardingApp = 'Servicenear' | 'Tobeonline';

export interface OnboardingAppConfig {
  /** IncomingAppName in incoming_apps_connection_config */
  app: OnboardingApp;
  /** Step keys logged through Seller/SaveSellerOnBoardingStatus as "<key> saved|skipped" */
  stepKeys: string[];
  /** Where the seller lands once onboarding is complete */
  landingRoute: string;
}

export interface PendingOnboarding {
  app: OnboardingApp;
  completedSteps: string[];
  /** Steps whose log entry is "<key> skipped" - shown as skipped on the finished screen */
  skippedSteps: string[];
  config: OnboardingAppConfig;
}

export type OnboardingCheck =
  | { state: 'pending', pending: PendingOnboarding }
  | { state: 'complete', config: OnboardingAppConfig | null }
  | { state: 'unknown' };

/** Always reachable whatever the plan enables (pay a pending bill) - used when the plan enables no page. */
const NO_ACCESS_LANDING = '/profile/SubscriptionDetails';

/**
 * Onboarding flows per connected app. The /onboarding page renders the flow of the seller's app.
 * To add an app (e.g. Tobeonline): add its config here and its component in onboarding.component.html.
 */
export const ONBOARDING_APPS: OnboardingAppConfig[] = [
  {
    app: 'Servicenear',
    stepKeys: SERVICENEAR_ONBOARDING_STEPS.map(step => step.key),
    landingRoute: '/servicenear/dashboard',
  },
];

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  constructor(
    private adminService: AdminService,
    private configService: ConfigService,
    private subscriptionsService: SubscriptionsService,
    private featureAccessService: FeatureAccessService,
  ) { }

  /**
   * Where the logged-in seller stands with app onboarding:
   *  - 'pending'  : steps left - `pending` holds the app and the steps already done
   *  - 'complete' : confirmed nothing to do (no app onboarding, or every step logged)
   *  - 'unknown'  : an API call failed - callers must not treat this as complete (it caused a
   *                 dashboard <-> onboarding redirect loop and loaded every dashboard API)
   * The app comes from IncomingApps (incoming_apps_connection_config), progress from the onboarding log.
   */
  public checkOnboarding(): Observable<OnboardingCheck> {
    return forkJoin({
      status: this.adminService.getSellerOnBoardingFlowStatus().pipe(catchError(() => of(null))),
      settings: this.configService.getSellerSettingsBySellerId({ filters: 'WebsiteTheme' }).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ status, settings }: any): OnboardingCheck => {
        if (settings?.Result != 'Success') {
          return { state: 'unknown' };
        }
        const config = this.getAppConfig(settings?.ResponseData?.IncomingApps);
        if (!config) {
          this.markComplete();   // no app onboarding for this seller
          return { state: 'complete', config: null };
        }
        if (status?.Result !== 'Success' || !Array.isArray(status?.ResponseData)) {
          return { state: 'unknown' };
        }
        const loggedKeys: string[] = status.ResponseData.map((step: any) => (step.OnBoardingStatus ?? '').split(' ')[0]);
        const completedSteps = this.getCompletedSteps(config.stepKeys, loggedKeys);
        const skippedSteps: string[] = status.ResponseData
          .map((step: any) => (step.OnBoardingStatus ?? '').split(' '))
          .filter((parts: string[]) => parts[1] === 'skipped')
          .map((parts: string[]) => parts[0]);
        if (config.stepKeys.every(key => completedSteps.includes(key))) {
          this.markComplete();
          return { state: 'complete', config };
        }
        return { state: 'pending', pending: { app: config.app, completedSteps, skippedSteps, config } };
      })
    );
  }

  /**
   * Where to go after login / registration - never a hard-coded dashboard:
   *  1. unfinished app onboarding           -> /onboarding
   *  2. the app's own landing page (ServiceNear -> /servicenear/dashboard)
   *  3. FeatureAccessService.getFirstAccessibleRoute(): the dashboard only if the plan enables it,
   *     otherwise the first enabled page of the plan's mobilemenu
   *  4. nothing enabled                     -> Subscription details (always reachable)
   * Subscription features are re-fetched (not the 24h cache) so a new login never uses another seller's plan.
   */
  public resolveLandingRoute(): Observable<string> {
    return forkJoin({
      check: this.checkOnboarding(),
      features: this.subscriptionsService.loadSubscriptionFeatures(true).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ check }) => {
        if (check.state === 'pending') {
          return '/onboarding';
        }
        return this.getLandingAfterOnboarding(check.state === 'complete' ? check.config : null);
      })
    );
  }

  /**
   * Landing once onboarding is done (subscription features already loaded):
   *  - the app's own page (ServiceNear -> /servicenear/dashboard) when the plan's FeatureAccess enables it
   *  - otherwise the first page the plan enables (never a page the plan has hidden / disabled)
   *  - nothing enabled -> Subscription details (always reachable)
   */
  public getLandingAfterOnboarding(config: OnboardingAppConfig | null): string {
    // ServiceNear key in the URL but the app check could not answer (API failed) -> still the ServiceNear app page
    const appLanding = config?.landingRoute
      ?? (/[?&]thirdpartyuserkey=/i.test(window.location.hash) ? ONBOARDING_APPS.find(app => app.app === 'Servicenear')?.landingRoute : undefined);
    if (appLanding && this.featureAccessService.canAccessUrl(appLanding)) {
      return appLanding;
    }
    return this.featureAccessService.getFirstAccessibleRoute() ?? NO_ACCESS_LANDING;
  }

  // ---- "already done" flag per seller, so the dashboard guard does not call the APIs on every visit ----
  private get completeFlagKey(): string {
    return 'onboardingComplete_' + (this.configService.getLoginDetails()?.Uuid ?? '');
  }

  public isMarkedComplete(): boolean {
    return localStorage.getItem(this.completeFlagKey) === 'true';
  }

  public markComplete() {
    localStorage.setItem(this.completeFlagKey, 'true');
  }

  /**
   * Steps are done in order, so a logged step means every step before it is done too.
   * Needed because SaveSellerOnBoardingStatus keeps ONE row per seller and overwrites it on every save:
   * after finishing, the log only holds the last step ("sn-uniqueid saved") - that still counts as complete.
   * Also works if the log keeps one row per step.
   */
  private getCompletedSteps(stepKeys: string[], loggedKeys: string[]): string[] {
    const lastLoggedIndex = Math.max(-1, ...loggedKeys.map(key => stepKeys.indexOf(key)));
    return stepKeys.slice(0, lastLoggedIndex + 1);
  }

  /** First app with an onboarding flow among the seller's connected apps. */
  public getAppConfig(incomingApps: string | null): OnboardingAppConfig | null {
    let apps: string[] = [];
    try {
      apps = JSON.parse(incomingApps ?? '[]') ?? [];
    } catch {
      apps = [];
    }
    const connected = apps.map(app => (app ?? '').toLowerCase());
    return ONBOARDING_APPS.find(config => connected.includes(config.app.toLowerCase())) ?? null;
  }
}
